// src/pages/NotificationsPage.tsx — notification preferences page.
//
// CODE-701 first wired this as a mocked prototype; the Phase 7b
// follow-up (this file's current state) is live against the real
// endpoints:
//
//   GET  /api/me/notification-preferences         (ENG-705)
//   PUT  /api/me/notification-preferences         (ENG-705)
//   PUT  /api/me/phone-number                     (ENG-706)
//   POST /api/me/test-email                       (ENG-708)
//   POST /api/me/test-sms                         (ENG-708)
//
// Wire shapes mirror PHASE-7B-CODE-START.md verbatim. Server enforces
// a 60-second cooldown per user on each test endpoint; the page
// mirrors that as a disabled button + countdown so the user never
// sees a 429 unless they manage to outrace the local timer (e.g. by
// reloading mid-cooldown). 400 / 429 / 502 errors surface as a
// friendly inline message.
//
// Initial phone state isn't on the GET prefs response — the page
// loads with an empty phone field and populates it from PUT phone /
// test-sms responses as the user interacts. A returning user can
// always re-enter to update; the server keeps the canonical value.
//
// USE_MOCK_NOTIFICATIONS stays in place as a single-line override for
// offline dev. False in production.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Field, Icons, Pill, TopBar } from "../components/ui";

// ─────────────────────────────────────────────────────────────────────────────
// Types — mirror the locked Phase 7 plan
// ─────────────────────────────────────────────────────────────────────────────

export type NotificationEvent =
  | "raise"
  | "urgent_raise"
  | "reply_on_my_issue"
  | "reply_to_assignee"
  // Phase 11.6 / CODE-709 — Quick Message inbox notifications. Backend
  // (ENG-709) fires `quick_message` on a fresh thread, `quick_message_reply`
  // when the message continues an existing one. Both default to disabled on
  // both channels so noisy DMs stay opt-in.
  | "quick_message"
  | "quick_message_reply"
  // Sign-In System (Build 2) — Group QuickMessage. Fires when a new message is
  // posted in a group you're in. Defaults off on both channels (in-app heather
  // banner is the primary signal). Mirror in api/src/notifications.ts.
  | "group_message";

export type NotificationChannel = "email" | "sms";

export interface NotificationPreference {
  eventType: NotificationEvent;
  channel: NotificationChannel;
  enabled: boolean;
  /** Server-side informational hint — true when this row reflects the
   *  default rather than a user override. The page doesn't render it
   *  today (could become a subtle "(default)" pill later); kept on the
   *  type so consumers don't strip it before round-tripping. */
  isDefault?: boolean;
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreference[];
  /** Carried alongside the toggles since the page renders both together —
   *  one round trip per page load. Added in ENG-705 follow-up `7185809`. */
  phoneNumber: string | null;
  phoneVerified: boolean;
}

export interface PhoneNumberResponse {
  ok: true;
  phoneNumber: string | null;
  phoneVerified: boolean;
}

export interface TestEmailResponse {
  ok: true;
  sentTo: string;
}

export interface TestSmsResponse {
  ok: true;
  sentTo: string;
  phoneVerified: true;
}

/** Shape of the wrapped error body returned by 400 / 429 / 502 from the
 *  test endpoints. The ApiError caught in the page surfaces `.message`
 *  which the api.ts wrapper has already pulled from this `error` field. */
export interface TestEndpointError {
  ok: false;
  error: string;
}

/** Grouping bucket for an EVENT_META row. Drives the section header
 *  rendered above the first row in each category so the 6-row grid reads
 *  as two logical sections rather than one long list. */
type NotificationCategory = "issues" | "quick_messages";

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  issues: "Issues",
  quick_messages: "Quick Messages",
};

// Friendly copy for each event. Order matters — surfaced top to bottom,
// grouped by `category` (a sub-heading renders before the first row of
// each category). When adding a new event, keep its category contiguous
// with the existing entries.
const EVENT_META: ReadonlyArray<{
  key: NotificationEvent;
  category: NotificationCategory;
  title: string;
  description: string;
  /** True when this row is only meaningful to managers — surface a
   *  small "Managers only" hint. Reporters can still toggle it but it
   *  won't fire for them server-side. */
  managerOnly?: boolean;
}> = [
  {
    key: "urgent_raise",
    category: "issues",
    title: "Urgent issue raised in my groups",
    description:
      "Someone raises an Urgent-priority issue in a group I manage. Email goes immediately; SMS only if my phone is verified.",
    managerOnly: true,
  },
  {
    key: "raise",
    category: "issues",
    title: "New issue raised in my groups",
    description:
      "Someone raises a non-urgent issue in a group I manage. Email only.",
    managerOnly: true,
  },
  {
    key: "reply_on_my_issue",
    category: "issues",
    title: "Reply on an issue I raised",
    description:
      "Anyone — manager, technician, or another reporter — adds a message to an issue I started.",
  },
  {
    key: "reply_to_assignee",
    category: "issues",
    title: "Reply on an issue assigned to me",
    description:
      "Someone replies on an issue where I'm the resolution assignee.",
  },
  // Phase 11.6 / CODE-709. Default off on both channels — the inbox dot
  // on the bottom nav is already telling you about these, so out-of-band
  // delivery is opt-in.
  {
    key: "quick_message",
    category: "quick_messages",
    title: "New quick message",
    description:
      "Someone sends you a new direct message in the Quick Message inbox. Disabled by default — turn on if you want it pushed out of band.",
  },
  {
    key: "quick_message_reply",
    category: "quick_messages",
    title: "Reply to your message",
    description:
      "Someone replies on a Quick Message thread you started. Disabled by default.",
  },
  // Sign-In System (Build 2) — Group QuickMessage. Lives in the Messages
  // section alongside the 1:1 rows. Disabled by default on both channels.
  {
    key: "group_message",
    category: "quick_messages",
    title: "Group messages",
    description:
      "A new message in a group you're in. Disabled by default — the in-app banner is the primary signal.",
  },
];

// Defaults per Phase 7 plan §Preferences (and Phase 11.6 / ENG-709 for the
// quick_message rows). No row in the table = these values apply. The page
// renders them as the "off-server" baseline so users who've never touched
// preferences see them populated. The defaults here must mirror
// api/src/notifications.ts `DEFAULTS` — drift means the SPA renders the
// wrong starting state for a brand-new user.
const DEFAULT_PREFS: Record<NotificationEvent, { email: boolean; sms: boolean }> = {
  raise:                { email: true,  sms: false },
  urgent_raise:         { email: true,  sms: true  },
  reply_on_my_issue:    { email: true,  sms: false },
  reply_to_assignee:    { email: true,  sms: false },
  quick_message:        { email: false, sms: false },
  quick_message_reply:  { email: false, sms: false },
  group_message:        { email: false, sms: false },
};

// ─────────────────────────────────────────────────────────────────────────────
// API wrapper — live against /api/me/* (ENG-705/706/708). Mock branch
// preserved behind the flag for offline dev only.
// ─────────────────────────────────────────────────────────────────────────────

const USE_MOCK_NOTIFICATIONS = false;

const TEST_COOLDOWN_MS = 60_000;

/** Session-scoped mock state used when USE_MOCK_NOTIFICATIONS is flipped
 *  back to true. Mutated by mock PUTs to give the offline-dev path a bit
 *  of persistence within the tab. */
const MOCK_STATE = {
  preferences: buildDefaultPreferences(),
  phoneNumber: null as string | null,
  phoneVerified: false,
};

function buildDefaultPreferences(): NotificationPreference[] {
  const out: NotificationPreference[] = [];
  for (const event of Object.keys(DEFAULT_PREFS) as NotificationEvent[]) {
    out.push({ eventType: event, channel: "email", enabled: DEFAULT_PREFS[event].email, isDefault: true });
    out.push({ eventType: event, channel: "sms",   enabled: DEFAULT_PREFS[event].sms,   isDefault: true });
  }
  return out;
}

async function fetchNotificationPreferences(): Promise<NotificationPreferencesResponse> {
  if (USE_MOCK_NOTIFICATIONS) {
    await new Promise((r) => setTimeout(r, 80));
    return {
      preferences: JSON.parse(JSON.stringify(MOCK_STATE.preferences)),
      phoneNumber: MOCK_STATE.phoneNumber,
      phoneVerified: MOCK_STATE.phoneVerified,
    };
  }
  return apiFetch<NotificationPreferencesResponse>("/api/me/notification-preferences");
}

async function putNotificationPreferences(
  preferences: NotificationPreference[],
): Promise<{ ok: true; updated: number }> {
  if (USE_MOCK_NOTIFICATIONS) {
    await new Promise((r) => setTimeout(r, 120));
    MOCK_STATE.preferences = preferences.map((p) => ({ ...p, isDefault: false }));
    return { ok: true, updated: preferences.length };
  }
  // Strip `isDefault` before sending — the server only consumes
  // (eventType, channel, enabled). Sending the informational flag back
  // would be harmless but it's cleaner to omit.
  return apiFetch<{ ok: true; updated: number }>(
    "/api/me/notification-preferences",
    {
      method: "PUT",
      body: JSON.stringify({
        preferences: preferences.map((p) => ({
          eventType: p.eventType,
          channel: p.channel,
          enabled: p.enabled,
        })),
      }),
    },
  );
}

async function putPhoneNumber(
  phoneNumber: string | null,
): Promise<{ phoneNumber: string | null; phoneVerified: boolean }> {
  if (USE_MOCK_NOTIFICATIONS) {
    await new Promise((r) => setTimeout(r, 120));
    MOCK_STATE.phoneNumber = phoneNumber;
    MOCK_STATE.phoneVerified = false;
    return { phoneNumber, phoneVerified: false };
  }
  const r = await apiFetch<PhoneNumberResponse>("/api/me/phone-number", {
    method: "PUT",
    body: JSON.stringify({ phoneNumber }),
  });
  return { phoneNumber: r.phoneNumber, phoneVerified: r.phoneVerified };
}

async function postTestEmail(): Promise<TestEmailResponse> {
  if (USE_MOCK_NOTIFICATIONS) {
    await new Promise((r) => setTimeout(r, 200));
    return { ok: true, sentTo: "yo***@nymr.co.uk" };
  }
  return apiFetch<TestEmailResponse>("/api/me/test-email", {
    method: "POST",
    body: "{}",
  });
}

async function postTestSms(): Promise<TestSmsResponse> {
  if (USE_MOCK_NOTIFICATIONS) {
    await new Promise((r) => setTimeout(r, 200));
    MOCK_STATE.phoneVerified = true;
    return { ok: true, sentTo: "+447***1576", phoneVerified: true };
  }
  return apiFetch<TestSmsResponse>("/api/me/test-sms", {
    method: "POST",
    body: "{}",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

type PrefMap = Record<NotificationEvent, { email: boolean; sms: boolean }>;

export function NotificationsPage() {
  const navigate = useNavigate();
  const { me, refresh: refreshMe } = useCurrentUser();

  const [prefs, setPrefs] = useState<PrefMap | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [initialPhone, setInitialPhone] = useState<string | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Quiet hours (Do Not Disturb). Loaded from me.preferences.dnd; saved via
  // PUT /api/me/preferences. Suppresses email/SMS in the window — in-app,
  // safety and urgent events are exempt (enforced server-side).
  const [dndEnabled, setDndEnabled] = useState(false);
  const [dndFrom, setDndFrom] = useState("22:00");
  const [dndTo, setDndTo] = useState("07:00");
  const [dndLoaded, setDndLoaded] = useState(false);
  const [savingDnd, setSavingDnd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchNotificationPreferences()
      .then((data) => {
        if (cancelled) return;
        setPrefs(serverToMap(data.preferences));
        // ENG-705 follow-up `7185809` ships phone state alongside the
        // toggles so we get both in one round trip. Populate the field,
        // pill, and the saved-baseline used by `phoneDirty`.
        setPhoneNumber(data.phoneNumber ?? "");
        setInitialPhone(data.phoneNumber);
        setPhoneVerified(data.phoneVerified);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? e.message
            : (e as Error)?.message ?? "Couldn't load preferences.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load saved quiet-hours from me.preferences once /api/me resolves.
  useEffect(() => {
    if (!me || dndLoaded) return;
    const d = me.preferences?.dnd;
    if (d) {
      setDndEnabled(!!d.enabled);
      if (d.from) setDndFrom(d.from);
      if (d.to) setDndTo(d.to);
    }
    setDndLoaded(true);
  }, [me, dndLoaded]);

  // ─── Test-send cooldown ticker ──────────────────────────────────────────
  // Mirrors the server's 60-second per-user cooldown so the buttons disable
  // with a countdown. The Map values are absolute ms-since-epoch deadlines.
  const [cooldownEnds, setCooldownEnds] = useState<{
    email?: number;
    sms?: number;
  }>({});
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const handle = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, []);
  function secondsLeft(channel: "email" | "sms"): number {
    const end = cooldownEnds[channel];
    if (!end) return 0;
    return Math.max(0, Math.ceil((end - nowMs) / 1000));
  }
  function startCooldown(channel: "email" | "sms") {
    setCooldownEnds((c) => ({ ...c, [channel]: Date.now() + TEST_COOLDOWN_MS }));
  }

  // Auto-dismiss the flash message
  useEffect(() => {
    if (!flash) return;
    const handle = window.setTimeout(() => setFlash(null), 2500);
    return () => window.clearTimeout(handle);
  }, [flash]);

  function toggle(event: NotificationEvent, channel: NotificationChannel) {
    setPrefs((cur) =>
      cur
        ? {
            ...cur,
            [event]: { ...cur[event], [channel]: !cur[event][channel] },
          }
        : cur,
    );
  }

  async function savePrefs() {
    if (!prefs) return;
    setSavingPrefs(true);
    setError(null);
    try {
      await putNotificationPreferences(mapToServer(prefs));
      setFlash("Notification preferences saved.");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : (e as Error)?.message ?? "Couldn't save preferences.",
      );
    } finally {
      setSavingPrefs(false);
    }
  }

  async function savePhone() {
    setSavingPhone(true);
    setError(null);
    try {
      const trimmed = phoneNumber.trim();
      const next = trimmed === "" ? null : trimmed;
      const r = await putPhoneNumber(next);
      setPhoneNumber(r.phoneNumber ?? "");
      setInitialPhone(r.phoneNumber);
      setPhoneVerified(r.phoneVerified);
      setFlash(
        next === null
          ? "Phone number cleared."
          : "Phone number saved. Tap Send test SMS below to verify it.",
      );
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : (e as Error)?.message ?? "Couldn't save phone number.",
      );
    } finally {
      setSavingPhone(false);
    }
  }

  async function saveDnd() {
    setSavingDnd(true);
    setError(null);
    try {
      await apiFetch("/api/me/preferences", {
        method: "PUT",
        body: JSON.stringify({ dnd: { enabled: dndEnabled, from: dndFrom, to: dndTo } }),
      });
      await refreshMe();
      setFlash(dndEnabled ? "Quiet hours saved." : "Quiet hours turned off.");
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : (e as Error)?.message ?? "Couldn't save quiet hours.",
      );
    } finally {
      setSavingDnd(false);
    }
  }

  // ─── Test send handlers ─────────────────────────────────────────────────

  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [sendingTestSms, setSendingTestSms] = useState(false);

  async function sendTestEmail() {
    setSendingTestEmail(true);
    setError(null);
    try {
      const r = await postTestEmail();
      setFlash(`Test email sent — check ${r.sentTo}.`);
      startCooldown("email");
    } catch (e) {
      // ApiError carries the server's `error` string (api.ts pulls it from
      // the body for non-2xx responses), so the user sees the friendly
      // "Please wait Ns..." / "No email address on file" / Graph error
      // messages directly. Start a cooldown on 429 anyway so the button
      // disables — the wait count is in the message text.
      const message =
        e instanceof ApiError
          ? e.message
          : (e as Error)?.message ?? "Couldn't send the test email.";
      setError(message);
      if (e instanceof ApiError && e.status === 429) startCooldown("email");
    } finally {
      setSendingTestEmail(false);
    }
  }

  async function sendTestSms() {
    setSendingTestSms(true);
    setError(null);
    try {
      const r = await postTestSms();
      // test-sms doubles as the verification — flip the pill optimistically.
      setPhoneVerified(true);
      setFlash(`Test SMS sent — check ${r.sentTo}. Your number is now verified.`);
      startCooldown("sms");
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.message
          : (e as Error)?.message ?? "Couldn't send the test SMS.";
      setError(message);
      if (e instanceof ApiError && e.status === 429) startCooldown("sms");
    } finally {
      setSendingTestSms(false);
    }
  }

  const phoneDirty = phoneNumber.trim() !== (initialPhone ?? "");

  return (
    <div>
      <TopBar
        title="Notifications"
        sub="Email & SMS preferences"
        onBack={() => navigate("/me")}
      />
      <div
        style={{
          padding: "18px 16px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {USE_MOCK_NOTIFICATIONS && (
          <div
            style={{
              background: "#FBEFCB",
              border: "1px solid rgba(176,132,64,0.40)",
              borderRadius: 12,
              padding: "10px 12px",
              fontFamily: F.body,
              fontSize: 12.5,
              color: "#7A540B",
              lineHeight: 1.4,
            }}
          >
            <b>Offline dev mode.</b> <code>USE_MOCK_NOTIFICATIONS</code> in{" "}
            <code>src/pages/NotificationsPage.tsx</code> is overriding the
            live API — saves only persist in this tab. Flip the constant
            to <code>false</code> for production.
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              padding: 12,
              borderRadius: 12,
              background: C.signalBg,
              border: "1px solid rgba(184,58,43,0.22)",
              fontFamily: F.body,
              fontSize: 13,
              color: C.signalDk,
            }}
          >
            <b>{error}</b>
          </div>
        )}

        {flash && (
          <div
            role="status"
            style={{
              padding: 12,
              borderRadius: 12,
              background: C.sageBg,
              border: `1px solid ${C.sageLt}`,
              color: C.sageDk,
              fontFamily: F.body,
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: 0.3,
            }}
          >
            {flash}
          </div>
        )}

        {/* Phone number card */}
        <Card>
          <Eyebrow>SMS phone number</Eyebrow>
          <p
            style={{
              fontFamily: F.body,
              fontSize: 13,
              color: C.inkSoft,
              margin: "6px 0 12px",
              lineHeight: 1.5,
            }}
          >
            Used only for Urgent-priority alerts. E.164 format, e.g.{" "}
            <code>+447441447886</code>. Leave blank to disable SMS entirely.
          </p>
          <Field
            label="Phone number"
            value={phoneNumber}
            onChange={setPhoneNumber}
            ph="+44…"
            type="tel"
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 10,
            }}
          >
            <Button
              variant="primary"
              disabled={savingPhone || !phoneDirty}
              onClick={savePhone}
            >
              {savingPhone ? "Saving…" : "Save phone number"}
            </Button>
            <Button
              variant="secondary"
              disabled={
                sendingTestSms ||
                secondsLeft("sms") > 0 ||
                (initialPhone === null && phoneNumber.trim() === "")
              }
              onClick={sendTestSms}
            >
              {sendingTestSms
                ? "Sending…"
                : secondsLeft("sms") > 0
                  ? `Send test SMS · wait ${secondsLeft("sms")}s`
                  : "Send test SMS"}
            </Button>
            {initialPhone && (
              <Pill tone={phoneVerified ? "sage" : "paper"} small>
                {phoneVerified ? "✓ Verified" : "Not verified"}
              </Pill>
            )}
          </div>
          {initialPhone === null && phoneNumber.trim() === "" && (
            <p
              style={{
                fontFamily: F.body,
                fontSize: 12,
                color: C.inkSoft,
                margin: "10px 0 0",
                lineHeight: 1.5,
                fontStyle: "italic",
              }}
            >
              Save a phone number above to unlock the test-SMS button. The
              test send is also how we verify your number.
            </p>
          )}
        </Card>

        {/* Quiet hours (Do Not Disturb) */}
        <Card>
          <Eyebrow>Quiet hours</Eyebrow>
          <p
            style={{
              fontFamily: F.body,
              fontSize: 13,
              color: C.inkSoft,
              margin: "6px 0 12px",
              lineHeight: 1.5,
            }}
          >
            During these hours, email and SMS notifications are held back. In-app
            messages, safety alerts (Lone Working) and urgent issues are always
            delivered. Times are your local time.
          </p>
          <button
            type="button"
            onClick={() => setDndEnabled((v) => !v)}
            aria-pressed={dndEnabled}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              borderRadius: 999,
              border: dndEnabled ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
              background: dndEnabled ? C.brassBg : C.card,
              color: dndEnabled ? C.brassDk : C.inkSoft,
              fontFamily: F.body,
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: 0.3,
              cursor: "pointer",
            }}
          >
            {dndEnabled ? "Quiet hours ON" : "Quiet hours OFF"}
          </button>
          {dndEnabled && (
            <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
              <label
                style={{
                  fontFamily: F.body,
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.inkSoft,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                From
                <input
                  type="time"
                  value={dndFrom}
                  onChange={(e) => setDndFrom(e.target.value)}
                  style={{
                    padding: "9px 11px",
                    borderRadius: 10,
                    border: `1px solid ${C.hairline}`,
                    fontFamily: F.body,
                    fontSize: 15,
                    color: C.ink,
                    background: C.card,
                  }}
                />
              </label>
              <label
                style={{
                  fontFamily: F.body,
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.inkSoft,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                To
                <input
                  type="time"
                  value={dndTo}
                  onChange={(e) => setDndTo(e.target.value)}
                  style={{
                    padding: "9px 11px",
                    borderRadius: 10,
                    border: `1px solid ${C.hairline}`,
                    fontFamily: F.body,
                    fontSize: 15,
                    color: C.ink,
                    background: C.card,
                  }}
                />
              </label>
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <Button variant="primary" disabled={savingDnd} onClick={saveDnd}>
              {savingDnd ? "Saving…" : "Save quiet hours"}
            </Button>
          </div>
        </Card>

        {/* Preferences table */}
        <Card>
          <Eyebrow>What I get notified about</Eyebrow>
          {loading || !prefs ? (
            <div
              style={{
                height: 220,
                borderRadius: 12,
                background: C.paperWarm,
                border: `1px solid ${C.hairlineLt}`,
                marginTop: 8,
              }}
              aria-hidden="true"
            />
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                {EVENT_META.map((meta, idx) => {
                  // CODE-709 — render a category sub-heading before the
                  // first row in each category. The EVENT_META list is
                  // already ordered with same-category entries contiguous
                  // so a simple "different from prior row?" check works.
                  const prev = idx > 0 ? EVENT_META[idx - 1] : null;
                  const showHeading = prev === null || prev.category !== meta.category;
                  return (
                    <div
                      key={meta.key}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {showHeading && (
                        <CategoryHeading label={CATEGORY_LABEL[meta.category]} />
                      )}
                      <EventRow
                        meta={meta}
                        email={prefs[meta.key].email}
                        sms={prefs[meta.key].sms}
                        smsBlocked={!phoneVerified && (initialPhone ?? "") === ""}
                        onToggle={(channel) => toggle(meta.key, channel)}
                      />
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  marginTop: 14,
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="primary"
                  disabled={savingPrefs}
                  onClick={savePrefs}
                >
                  {savingPrefs ? "Saving…" : "Save preferences"}
                </Button>
                <Button
                  variant="secondary"
                  disabled={sendingTestEmail || secondsLeft("email") > 0}
                  onClick={sendTestEmail}
                >
                  {sendingTestEmail
                    ? "Sending…"
                    : secondsLeft("email") > 0
                      ? `Send test email · wait ${secondsLeft("email")}s`
                      : "Send test email"}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-views
// ─────────────────────────────────────────────────────────────────────────────

function CategoryHeading({ label }: { label: string }) {
  // CODE-709 — small section header rendered above the first row of each
  // notification category inside the preferences card. Uses the same
  // typographic treatment as the page-level Eyebrow but downscaled and
  // with a hairline underline so it reads as a sub-section rather than a
  // top-level label.
  return (
    <div
      style={{
        fontFamily: F.body,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: C.inkSoft,
        padding: "2px 2px 6px",
        borderBottom: `1px solid ${C.hairlineLt}`,
        marginTop: 4,
      }}
    >
      {label}
    </div>
  );
}

function EventRow({
  meta,
  email,
  sms,
  smsBlocked,
  onToggle,
}: {
  meta: {
    key: NotificationEvent;
    title: string;
    description: string;
    managerOnly?: boolean;
  };
  email: boolean;
  sms: boolean;
  smsBlocked: boolean;
  onToggle: (channel: NotificationChannel) => void;
}) {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 12,
        background: C.paperWarm,
        border: `1px solid ${C.hairlineLt}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 4,
        }}
      >
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 15,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: -0.1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {meta.title}
        </div>
        {meta.managerOnly && (
          <Pill tone="heather" small>
            Managers only
          </Pill>
        )}
      </div>
      <p
        style={{
          fontFamily: F.body,
          fontSize: 12.5,
          color: C.inkSoft,
          margin: "0 0 10px",
          lineHeight: 1.5,
        }}
      >
        {meta.description}
      </p>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <ChannelToggle
          channel="email"
          label="Email"
          icon={<Icons.Mail s={14} />}
          enabled={email}
          onClick={() => onToggle("email")}
        />
        <ChannelToggle
          channel="sms"
          label={smsBlocked ? "SMS (set phone first)" : "SMS"}
          icon={<Icons.Phone s={14} />}
          enabled={sms}
          disabled={smsBlocked}
          onClick={() => onToggle("sms")}
        />
      </div>
    </div>
  );
}

function ChannelToggle({
  label,
  icon,
  enabled,
  disabled = false,
  onClick,
}: {
  channel: NotificationChannel;
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      aria-pressed={enabled}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 11px",
        borderRadius: 999,
        background: enabled ? C.brassDk : C.card,
        color: enabled ? C.brassGlow : C.inkSoft,
        border: `1px solid ${enabled ? C.brassDk : C.hairline}`,
        fontFamily: F.body,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        boxShadow: enabled ? "inset 0 1px 0 rgba(244,226,168,0.3)" : "none",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function serverToMap(prefs: NotificationPreference[]): PrefMap {
  // Start from defaults so any event the server omits inherits the default.
  const out: PrefMap = {
    raise:                { ...DEFAULT_PREFS.raise },
    urgent_raise:         { ...DEFAULT_PREFS.urgent_raise },
    reply_on_my_issue:    { ...DEFAULT_PREFS.reply_on_my_issue },
    reply_to_assignee:    { ...DEFAULT_PREFS.reply_to_assignee },
    quick_message:        { ...DEFAULT_PREFS.quick_message },
    quick_message_reply:  { ...DEFAULT_PREFS.quick_message_reply },
    group_message:        { ...DEFAULT_PREFS.group_message },
  };
  for (const p of prefs) {
    if (out[p.eventType]) out[p.eventType][p.channel] = p.enabled;
  }
  return out;
}

function mapToServer(map: PrefMap): NotificationPreference[] {
  const out: NotificationPreference[] = [];
  for (const event of Object.keys(map) as NotificationEvent[]) {
    out.push({ eventType: event, channel: "email", enabled: map[event].email });
    out.push({ eventType: event, channel: "sms",   enabled: map[event].sms });
  }
  return out;
}
